import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { CardFooterComponent } from '../../../shared/card-footer/card-footer.component';
import { HeaderComponent } from '../../../shared/header/header.component';
import { ApiResponse } from '../../../shared/interfaces/api-response.interface';
import { PasswordInputComponent } from '../../../shared/password-input/password-input.component';
import { EmployerAuthService, EmployerLoginResponse, EmployerOTPResponse } from '../../../shared/services/employer-auth.service';
import { ErrorMessageService } from '../../../shared/services/error-message.service';
import { RedirectHelperService } from '../../../customidp/shared/services/redirect-helper.service';
import { ActiveSessionService } from '../../../shared/services/active-session.service';

@Component({
  selector: 'app-login',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    HeaderComponent,
    CardFooterComponent,
    PasswordInputComponent,
    TranslateModule,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
})
export class LoginComponent implements OnInit {
  policyOrEmail = '';
  password = '';
  policyOrEmailErrors: string[] = [];
  passwordErrors: string[] = [];
  loginError = '';
  policyOrEmailTouched = false;
  passwordTouched = false;
  loading = false;
  remainingAttempts: number | undefined;

  constructor(
    private titleService: Title,
    private router: Router,
    private translate: TranslateService,
    private employerAuthService: EmployerAuthService,
    private errorMessageService: ErrorMessageService,
    private redirectHelper: RedirectHelperService,
    private activeSession: ActiveSessionService
  ) { }

  ngOnInit(): void {
    // PLAI-8323: skip form if another tab already signed in.
    if (this.activeSession.hasValidToken()) {
      this.activeSession.redirectToEmployerPortalHome();
      return;
    }

    this.titleService.setTitle('Employer | Login');
    this.translate.use(this.translate.currentLang || 'en');
  }

  validatePolicyOrEmail() {
    this.policyOrEmailErrors = [];
    this.policyOrEmailTouched = true;
    this.loginError = '';

    if (!this.policyOrEmail) {
      this.policyOrEmailErrors.push('EMPLOYER.LOGIN.EMAIL_REQUIRED');
      return;
    }
  }

  validatePassword() {
    this.passwordErrors = [];
    this.passwordTouched = true;
    this.loginError = '';

    if (!this.password) {
      this.passwordErrors.push('EMPLOYER.LOGIN.PASSWORD_REQUIRED');
    }
  }

  isFormValid(): boolean {
    const isPolicyOrEmailValid = this.policyOrEmail.length > 0 && this.policyOrEmailErrors.length === 0;
    const isPasswordValid = this.password.length >= 1;
    return isPolicyOrEmailValid && isPasswordValid;
  }

  login() {
    this.loginError = '';
    this.remainingAttempts = undefined;

    // Mark fields as touched to show validation errors
    this.policyOrEmailTouched = true;
    this.passwordTouched = true;

    // Run validations to show required field errors
    this.validatePolicyOrEmail();
    this.validatePassword();

    if (!this.isFormValid()) {
      return;
    }

    this.loading = true;

    this.employerAuthService.validateCredentials(this.policyOrEmail, this.password).subscribe({
      next: (response: ApiResponse<EmployerLoginResponse | EmployerOTPResponse>) => {
        this.loading = false;
        if (this.errorMessageService.isSuccessResponse(response)) {
          if (response.data && 'authCode' in response.data) {
            this.handleLoginSuccess(response.data as EmployerOTPResponse, 'employer');
            return;
          }
          // Store session data for verify component
          if (response.data) {
            const sessionData = {
              sessionId: (response.data as EmployerLoginResponse).sessionId,
              trxId: (response.data as EmployerLoginResponse).trxId,
              phone: (response.data as EmployerLoginResponse).phone,
              email: (response.data as EmployerLoginResponse).email,
              policies: (response.data as EmployerLoginResponse).policies || []
            };
            sessionStorage.setItem('employerSessionData', JSON.stringify(sessionData));
          }

          this.router.navigate(['/employer/account/verify'], {
            queryParams: { type: 'login' }
          });
        } else {
          this.handleLoginError(response);
        }
      },
      error: (error: any) => {
        this.loading = false;
        this.handleLoginError(error.error || error);
      }
    });
  }

  private handleLoginSuccess(data: EmployerOTPResponse, userType: 'employer' | 'employee'): void {
    const defaultRole = 'N_HR';
    const selectedRole = data?.selectedRole || defaultRole;
    const authCode = data?.authCode;
    const defaultDashboard = '/employer/dashboard';

    if (environment.ENV_ACTIVE_PROFILE === 'customIDP' && authCode) {
      this.redirectHelper.redirectByRole(authCode, selectedRole);
    } else {
      this.router.navigate([defaultDashboard]);
    }
  }

  handleLoginError(error: any) {
    // Check for lockout and attempts info
    if (
      (error.errorCode === '1021' || error.errorCode === 'accountTemporarilyDisabled') &&
      error.data && error.data.remainingLockTimeSeconds > 0
    ) {
      const remaining = error.data.remainingLockTimeSeconds;
      const minutes = Math.floor(remaining / 60);
      const seconds = remaining % 60;
      this.translate.get('error.accountTemporarilyDisabled', { minutes, seconds }).subscribe(message => {
        this.loginError = message;
      });
    } else if (error.data && error.data.remainingAttempts !== undefined && error.data.remainingAttempts !== null) {
      this.remainingAttempts = error.data.remainingAttempts;
      this.errorMessageService.getErrorMessage(error.errorCode).subscribe(message => {
        const plural = this.remainingAttempts === 1 ? '' : 's';
        this.translate.get('error.remainingAttempts', { count: this.remainingAttempts, plural }).subscribe(attemptMsg => {
          this.loginError = `${message} ${attemptMsg}`;
        });
      });
    } else if (error.errorCode) {
      this.errorMessageService.getErrorMessage(error.errorCode).subscribe(message => {
        this.loginError = message;
      });
    } else {
      this.translate.get('error.internalServerError').subscribe(message => {
        this.loginError = message;
      });
    }
  }
}
